export type AtLeast1<T> = [T, ...T[]];
export const isAtLeast1 = <T>(arr: T[]): arr is AtLeast1<T> => arr.length >= 1;

export type AtLeast2<T> = [T, T, ...T[]];
export const isAtLeast2 = <T>(arr: T[]): arr is AtLeast2<T> => arr.length >= 2;

export type AtLeast3<T> = [T, T, T, ...T[]];
export const isAtLeast3 = <T>(arr: T[]): arr is AtLeast3<T> => arr.length >= 3;

/**
 * Utility class for performing operations on tuples with at least N elements, ensuring that operations like `map`, `flatMap`, and `filter` provide type guarantees.
 *
 * Mapping over a tuple that has at least 1 item according to its type, would otherwise result in string[] instead of [string, ...string[]] and so the minimumItems type restriction is lost.
 *
 * @example
 * const input: AtLeast1<string> = ['a', 'b', 'c'];
 * const uppercased = minItemsArrayUtils.mapAtLeast1(input, str => str.toUpperCase());
 * // the uppercased variable hase retained its original type info: AtLeast1<string> and did not fall back to string[]
 * console.log(uppercased); // ['A', 'B', 'C']
 */
export class minItemsArrayUtils {
  /**
   * Maps over a tuple with at least 1 item.
   */
  static mapAtLeast1<T, U>(
    input: AtLeast1<T>,
    fn: (item: T, index: number, array: AtLeast1<T>) => U,
  ): AtLeast1<U> {
    const [a, ...rest] = input;
    return [fn(a, 0, input), ...rest.map((v, i) => fn(v, i + 1, input))];
  }

  /**
   * Maps over a tuple with at least 2 items.
   */
  static mapAtLeast2<T, U>(
    input: AtLeast2<T>,
    fn: (item: T, index: number, array: AtLeast2<T>) => U,
  ): AtLeast2<U> {
    const [a, b, ...rest] = input;
    return [fn(a, 0, input), fn(b, 1, input), ...rest.map((v, i) => fn(v, i + 2, input))];
  }

  /**
   * Maps over a tuple with at least 3 items.
   */
  static mapAtLeast3<T, U>(
    input: AtLeast3<T>,
    fn: (item: T, index: number, array: AtLeast3<T>) => U,
  ): AtLeast3<U> {
    const [a, b, c, ...rest] = input;
    return [
      fn(a, 0, input),
      fn(b, 1, input),
      fn(c, 2, input),
      ...rest.map((v, i) => fn(v, i + 3, input)),
    ];
  }
}
